import { forwardRef, type MouseEventHandler, type ReactNode } from 'react'
import { LoadingDots } from '@/components/Loading/LoadingDots'
import { ButtonAction, ButtonShape, ButtonSize, ButtonState } from '@/constants/buttons'
import { cn } from '@/utils/shadcn/utils'

export type ButtonStateConfig = {
  isDisabled?: boolean
  isLoading?: boolean
}

type ElementProps = {
  children?: ReactNode
  href?: string
  onClick?: MouseEventHandler<HTMLButtonElement> | MouseEventHandler<HTMLAnchorElement>
  slotLeft?: ReactNode
  slotRight?: ReactNode
  state?: ButtonState | ButtonStateConfig
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
  noOpacityColors?: boolean
}

type StyleProps = {
  action?: ButtonAction
  size?: ButtonSize | 'sm' | 'lg' // Allow string sizes for backwards compatibility
  shape?: ButtonShape
  variant?: 'outline' | 'default' | 'ghost' // Add variant for backwards compatibility
  className?: string
}

export type ButtonProps = ElementProps & StyleProps

// Base button styles with our new consistent styling
const baseButtonClasses =
  'inline-flex items-center justify-center gap-2 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50'

// New consistent button styling
const newButtonClasses = (noOpacityColors?: boolean) =>
  noOpacityColors
    ? 'px-3 py-1 bg-primary text-white border border-primary rounded text-xs hover:bg-primary/90 transition-colors'
    : 'px-3 py-1 bg-primary/30 text-white border border-primary/40 rounded text-xs hover:bg-primary/40 transition-colors'

// Size variants
const sizeVariants = {
  [ButtonSize.XSmall]: 'h-6 px-2 text-xs',
  [ButtonSize.Small]: 'h-8 px-3 text-xs',
  [ButtonSize.Base]: 'h-9 px-4 text-sm',
  [ButtonSize.Medium]: 'h-10 px-6 text-sm',
  [ButtonSize.Large]: 'h-11 px-8 text-base',
  [ButtonSize.XLarge]: 'h-12 px-10 text-base',
  // String sizes for backwards compatibility
  sm: 'h-8 px-3 text-xs',
  lg: 'h-11 px-8 text-base',
}

// Shape variants
const shapeVariants = {
  [ButtonShape.Rectangle]: 'rounded',
  [ButtonShape.Circle]: 'rounded-full aspect-square',
  [ButtonShape.Square]: 'rounded aspect-square',
  [ButtonShape.Pill]: 'rounded-full',
}

// Variant styles for backwards compatibility
const variantStyles = (noOpacityColors?: boolean) => ({
  default: newButtonClasses(noOpacityColors),
  outline: noOpacityColors
    ? 'px-3 py-1 bg-transparent text-white border border-primary rounded text-xs hover:bg-primary/10 transition-colors'
    : 'px-3 py-1 bg-transparent text-white border border-primary/40 rounded text-xs hover:bg-primary/20 transition-colors',
  ghost:
    'px-3 py-1 bg-transparent text-white border border-transparent rounded text-xs hover:bg-primary/20 transition-colors',
})

// Action variants (keeping some for backwards compatibility, but defaulting to our new style)
const actionVariants = (noOpacityColors?: boolean) => ({
  [ButtonAction.Primary]: newButtonClasses(noOpacityColors),
  [ButtonAction.Secondary]: newButtonClasses(noOpacityColors),
  [ButtonAction.Base]: newButtonClasses(noOpacityColors),
  [ButtonAction.Create]: noOpacityColors
    ? 'px-3 py-1 bg-green-500 text-white border border-green-500 rounded text-xs hover:bg-green-600 transition-colors'
    : 'px-3 py-1 bg-green-500/30 text-white border border-green-500/40 rounded text-xs hover:bg-green-500/40 transition-colors',
  [ButtonAction.Destroy]: noOpacityColors
    ? 'px-3 py-1 bg-red-500 text-white border border-red-500 rounded text-xs hover:bg-red-600 transition-colors'
    : 'px-3 py-1 bg-red-500/30 text-white border border-red-500/40 rounded text-xs hover:bg-red-500/40 transition-colors',
  [ButtonAction.Reset]: noOpacityColors
    ? 'px-3 py-1 bg-orange-500 text-orange-500 border border-orange-500 rounded text-xs hover:bg-danger/40 transition-colors'
    : 'px-3 py-1 bg-orange-500/30 text-orange-500 border border-orange-500 rounded text-xs hover:bg-orange-500/40 transition-colors',
  [ButtonAction.Navigation]:
    'px-3 py-1 bg-transparent text-white border border-transparent rounded text-xs hover:bg-primary/20 transition-colors',
})

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      action = ButtonAction.Primary,
      size = ButtonSize.Base,
      shape = ButtonShape.Rectangle,
      variant,
      state: stateConfig = ButtonState.Default,
      disabled: propDisabled,
      noOpacityColors = false,
      type = 'button',
      className,
      children,
      slotLeft = null,
      slotRight = null,
      href,
      onClick,
      ...otherProps
    },
    ref,
  ) => {
    const state: Record<string, boolean | undefined> =
      typeof stateConfig === 'string'
        ? { [stateConfig as ButtonState]: true }
        : {
            [ButtonState.Loading]: stateConfig.isLoading,
            [ButtonState.Disabled]: stateConfig.isDisabled,
          }

    const isDisabled = state[ButtonState.Disabled] || state[ButtonState.Loading] || propDisabled
    const isLoading = state[ButtonState.Loading]

    // Combine all classes - prioritize variant over action if both are provided
    const buttonClasses = cn(
      baseButtonClasses,
      variant ? variantStyles(noOpacityColors)[variant] : actionVariants(noOpacityColors)[action],
      sizeVariants[size as keyof typeof sizeVariants],
      shapeVariants[shape],
      className,
    )

    const content = isLoading ? (
      <LoadingDots size={3} />
    ) : (
      <>
        {slotLeft}
        {children}
        {slotRight}
      </>
    )

    if (href) {
      return (
        <a
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
          href={href}
          className={buttonClasses}
          onClick={onClick as MouseEventHandler<HTMLAnchorElement>}
          {...otherProps}
        >
          {content}
        </a>
      )
    }

    return (
      <button
        ref={ref as React.ForwardedRef<HTMLButtonElement>}
        type={type}
        disabled={isDisabled}
        className={buttonClasses}
        onClick={onClick as MouseEventHandler<HTMLButtonElement>}
        {...otherProps}
      >
        {content}
      </button>
    )
  },
)

Button.displayName = 'Button'
